import {
  VIDEO_MATERIAL_MEDIA_TYPE,
  VIDEO_PRODUCTION_DEFAULT_ADAPT_TYPE,
  VIDEO_PRODUCTION_SLOT_KIND,
} from '../constants.js';

export const ORDER_ASSET_CATEGORY_ID = '__order_assets__';
export const SLOT_GROUP_VALUES_KEY = '__groups';
export const VIDEO_PRODUCTION_SLOT_ENTRY_TYPE = {
  GROUP: 'group',
  SLOT: 'slot',
};

const MEDIA_SLOT_KINDS = new Set([
  VIDEO_PRODUCTION_SLOT_KIND.IMAGE,
  VIDEO_PRODUCTION_SLOT_KIND.VIDEO,
]);
const SLOT_KIND_LABELS = {
  [VIDEO_PRODUCTION_SLOT_KIND.IMAGE]: '图片',
  [VIDEO_PRODUCTION_SLOT_KIND.VIDEO]: '视频',
  [VIDEO_PRODUCTION_SLOT_KIND.TEXT]: '文字',
};
const DEFAULT_CONTENT_HINT_EMPTY = '未填则不显示';
const DEFAULT_CONTENT_HINT_PREFIX = '未填使用模板默认内容：';
const DEFAULT_CONTENT_SEPARATOR = ' / ';
const ASSET_PICKER_SELECTED_ID_PREFIX = 'selected:';
const ASSET_PICKER_AVAILABLE_ID_PREFIX = 'asset:';

export const isMediaSlotKind = (kind) => MEDIA_SLOT_KINDS.has(kind);

const normalizeConfig = (config) =>
  config && typeof config === 'object' && !Array.isArray(config)
    ? { ...config }
    : {};

const normalizeAdaptTypeValue = (value) => {
  if (value === undefined || value === null || value === '') {
    return String(VIDEO_PRODUCTION_DEFAULT_ADAPT_TYPE);
  }
  const parsed = Number(value);
  return [0, 1, 2, 3].includes(parsed)
    ? String(parsed)
    : String(VIDEO_PRODUCTION_DEFAULT_ADAPT_TYPE);
};

const normalizeRuntimeConfigValue = (configKey, value) => {
  if (value === undefined || value === null || value === '') return undefined;
  if (configKey === 'adapt_type') return Number(normalizeAdaptTypeValue(value));
  return String(value).trim();
};

export const getSlotMergedConfig = (slot, slotValues = {}) => ({
  ...normalizeConfig(slot?.config),
  ...normalizeConfig(slotValues?.[slot?.key]?.config),
});

export const getSlotAdaptTypeValue = (slot, slotValues = {}) =>
  normalizeAdaptTypeValue(getSlotMergedConfig(slot, slotValues).adapt_type);

export const getSlotFontFileValue = (slot, slotValues = {}) =>
  String(getSlotMergedConfig(slot, slotValues).font_file || '');

export const getSlotValueKeys = (kind) => {
  if (kind === VIDEO_PRODUCTION_SLOT_KIND.IMAGE) {
    return ['image_id', 'media_url', 'oss_url', 'url', 'image_url'];
  }
  if (kind === VIDEO_PRODUCTION_SLOT_KIND.VIDEO) {
    return ['oss_url', 'media_url', 'url'];
  }
  return ['text'];
};

export const getSlotValueKey = (kind) => getSlotValueKeys(kind)[0];

export const getAssetId = (asset) =>
  asset?.id || asset?.image_id || asset?.video_id || asset?.url || asset?.content || '';

export const getAssetUrl = (asset) =>
  asset?.content || asset?.url || asset?.image_url || asset?.media_url || asset?.oss_url || '';

export const getVideoCoverUrl = (asset) =>
  asset?.cover_url ||
  asset?.cover ||
  asset?.thumbnail_url ||
  asset?.thumbnail ||
  asset?.poster ||
  asset?.image_url ||
  '';

export const getAssetLabel = (asset, fallback) =>
  asset?.description || asset?.name || asset?.title || fallback;

export const getSlotGroupKey = (slot) => String(slot?.group_key || '').trim();

const getSlotKindLabel = (kind) => SLOT_KIND_LABELS[kind] || '插槽';

const getSlotGroupDisplayName = (group, kind, groupKey) => {
  const name = String(group?.name || '').trim();
  return name && name !== groupKey ? name : `${getSlotKindLabel(kind)}组`;
};

export const getSlotGroups = (slotConfig = [], slotGroupConfig = {}) => {
  const groups = [];
  const seen = new Set();
  for (const slot of Array.isArray(slotConfig) ? slotConfig : []) {
    const groupKey = getSlotGroupKey(slot);
    if (!groupKey || seen.has(groupKey)) continue;
    seen.add(groupKey);
    const group = slotGroupConfig?.[groupKey] || {};
    const kind = group.kind || slot.kind;
    groups.push({
      key: groupKey,
      name: getSlotGroupDisplayName(group, kind, groupKey),
      kind,
      config: group.config || {},
      runtime: group.runtime || {},
      slots: slotConfig.filter((item) => getSlotGroupKey(item) === groupKey),
    });
  }
  return groups;
};

export const getUngroupedSlots = (slotConfig = []) =>
  (Array.isArray(slotConfig) ? slotConfig : []).filter(
    (slot) => !getSlotGroupKey(slot)
  );

export const buildProductionSlotEntries = ({
  slotConfig = [],
  slotGroupConfig = {},
} = {}) => {
  const groupsByKey = new Map(
    getSlotGroups(slotConfig, slotGroupConfig).map((group) => [group.key, group])
  );
  const emittedGroupKeys = new Set();

  return (Array.isArray(slotConfig) ? slotConfig : []).flatMap((slot) => {
    const groupKey = getSlotGroupKey(slot);
    const group = groupsByKey.get(groupKey);

    if (group) {
      if (emittedGroupKeys.has(groupKey)) return [];
      emittedGroupKeys.add(groupKey);
      return [
        {
          type: VIDEO_PRODUCTION_SLOT_ENTRY_TYPE.GROUP,
          id: `group:${groupKey}`,
          group,
        },
      ];
    }

    return [
      {
        type: VIDEO_PRODUCTION_SLOT_ENTRY_TYPE.SLOT,
        id: `slot:${slot.key || ''}`,
        slot,
      },
    ];
  });
};

export const buildSlotValue = (
  slot,
  value,
  valueKey = getSlotValueKey(slot.kind),
  config
) => {
  const normalizedConfig = normalizeConfig(config);
  return {
    kind: slot.kind,
    [valueKey]: value,
    ...(Object.keys(normalizedConfig).length
      ? { config: normalizedConfig }
      : {}),
  };
};

export const buildGroupAssetItem = (kind, asset) => ({
  kind,
  [asset.valueKey || getSlotValueKey(kind)]: asset.value,
});

export const getGroupValues = (slotValues = {}) =>
  slotValues?.[SLOT_GROUP_VALUES_KEY] &&
  typeof slotValues[SLOT_GROUP_VALUES_KEY] === 'object' &&
  !Array.isArray(slotValues[SLOT_GROUP_VALUES_KEY])
    ? slotValues[SLOT_GROUP_VALUES_KEY]
    : {};

export const getGroupValue = (slotValues = {}, groupKey) =>
  getGroupValues(slotValues)[groupKey] || {};

export const getGroupSelectedItems = (slotValues = {}, groupKey) =>
  Array.isArray(getGroupValue(slotValues, groupKey).items)
    ? getGroupValue(slotValues, groupKey).items
    : [];

export const getGroupTextValue = (slotValues = {}, groupKey) =>
  String(getGroupValue(slotValues, groupKey).text || '');

const getGroupItemValue = (kind, item = {}) =>
  getSlotValueKeys(kind)
    .map((key) => item[key])
    .find((value) => value !== undefined && value !== null && value !== '') || '';

export const getIncompleteImageGroups = ({
  slotConfig = [],
  slotGroupConfig = {},
  slotValues = {},
} = {}) =>
  getSlotGroups(slotConfig, slotGroupConfig)
    .filter((group) => group.kind === VIDEO_PRODUCTION_SLOT_KIND.IMAGE)
    .map((group) => {
      const selectedCount = getGroupSelectedItems(slotValues, group.key).length;
      return {
        ...group,
        selectedCount,
        missingCount: Math.max(group.slots.length - selectedCount, 0),
      };
    })
    .filter((group) => group.missingCount > 0);

export const getTextCharacterCount = (value) =>
  Array.from(String(value || '').replace(/\r\n?|\n/g, '')).length;

export const getTextInputMetrics = (value) => ({
  characterCount: getTextCharacterCount(value),
});

export const formatDefaultContentHint = (defaultContent) => {
  const contents = (Array.isArray(defaultContent)
    ? defaultContent
    : [defaultContent]
  )
    .map((content) => String(content || '').trim())
    .filter(Boolean);

  return contents.length
    ? `${DEFAULT_CONTENT_HINT_PREFIX}${contents.join(DEFAULT_CONTENT_SEPARATOR)}`
    : DEFAULT_CONTENT_HINT_EMPTY;
};

const normalizeTextInput = (value) =>
  String(value || '').replace(/\r\n?/g, '\n');

const splitTextByLines = (value) => {
  const text = normalizeTextInput(value);
  if (text === '') return [];
  return text.split('\n');
};

export const getTextGroupMetrics = (value) => {
  const segments = splitTextByLines(value);
  return {
    segmentCount: segments.length,
    longestSegmentLength: Math.max(
      0,
      ...segments.map(getTextCharacterCount)
    ),
  };
};

export const prioritizeSelectedAssets = ({
  allAssets = [],
  visibleAssets = [],
  selectedValues = [],
} = {}) => {
  const allAssetsByValue = new Map(
    (Array.isArray(allAssets) ? allAssets : []).map((asset) => [
      String(asset?.value || ''),
      asset,
    ])
  );
  const emittedValues = new Set();
  const result = [];

  const appendAsset = (asset) => {
    const value = String(asset?.value || '');
    if (!value || emittedValues.has(value)) return;
    emittedValues.add(value);
    result.push(asset);
  };

  (Array.isArray(selectedValues) ? selectedValues : []).forEach((value) => {
    appendAsset(allAssetsByValue.get(String(value || '')));
  });
  (Array.isArray(visibleAssets) ? visibleAssets : []).forEach(appendAsset);

  return result;
};

export const buildAssetPickerEntries = ({
  allAssets = [],
  visibleAssets = [],
  selectedValues = [],
} = {}) => {
  const allAssetsByValue = new Map(
    (Array.isArray(allAssets) ? allAssets : []).map((asset) => [
      String(asset?.value || ''),
      asset,
    ])
  );
  const normalizedSelectedValues = (Array.isArray(selectedValues)
    ? selectedValues
    : []
  ).map(String);
  const selectedValueSet = new Set(normalizedSelectedValues);
  const entries = normalizedSelectedValues.flatMap((value, index) => {
    const asset = allAssetsByValue.get(value);
    return asset
      ? [{
          id: `${ASSET_PICKER_SELECTED_ID_PREFIX}${index}`,
          asset,
          selectedIndex: index,
          order: index + 1,
        }]
      : [];
  });
  const emittedAvailableValues = new Set();

  for (const asset of Array.isArray(visibleAssets) ? visibleAssets : []) {
    const value = String(asset?.value || '');
    if (
      !value ||
      selectedValueSet.has(value) ||
      emittedAvailableValues.has(value)
    ) {
      continue;
    }
    emittedAvailableValues.add(value);
    entries.push({
      id: `${ASSET_PICKER_AVAILABLE_ID_PREFIX}${value}`,
      asset,
      selectedIndex: null,
      order: null,
    });
  }

  return entries;
};

const splitTextBySlotCount = (value, slotCount) => {
  const parts = splitTextByLines(value);
  if (!slotCount) return parts;
  return parts.slice(0, slotCount + 1);
};

const getTextSlotDefaultContent = (slot) =>
  slot?.kind === VIDEO_PRODUCTION_SLOT_KIND.TEXT
    ? String(slot?.default_content || '').trim()
    : '';

export const toggleGroupAsset = ({ slotValues = {}, group, asset }) => {
  const currentGroups = getGroupValues(slotValues);
  const currentValue = currentGroups[group.key] || { kind: group.kind };
  const currentItems = Array.isArray(currentValue.items)
    ? currentValue.items
    : [];
  const assetItem = buildGroupAssetItem(group.kind, asset);
  const assetValue = getGroupItemValue(group.kind, assetItem);
  const exists = currentItems.some(
    (item) => String(getGroupItemValue(group.kind, item)) === String(assetValue)
  );
  const nextItems = exists
    ? currentItems.filter(
        (item) => String(getGroupItemValue(group.kind, item)) !== String(assetValue)
      )
    : [...currentItems, assetItem];

  return {
    ...slotValues,
    [SLOT_GROUP_VALUES_KEY]: {
      ...currentGroups,
      [group.key]: {
        ...currentValue,
        kind: group.kind,
        items: nextItems,
      },
    },
  };
};

export const appendGroupAsset = ({ slotValues = {}, group, asset }) => {
  const currentGroups = getGroupValues(slotValues);
  const currentValue = currentGroups[group.key] || { kind: group.kind };
  const currentItems = Array.isArray(currentValue.items)
    ? currentValue.items
    : [];
  if (currentItems.length >= group.slots.length) return slotValues;

  return {
    ...slotValues,
    [SLOT_GROUP_VALUES_KEY]: {
      ...currentGroups,
      [group.key]: {
        ...currentValue,
        kind: group.kind,
        items: [...currentItems, buildGroupAssetItem(group.kind, asset)],
      },
    },
  };
};

export const removeGroupAsset = ({ slotValues = {}, group, itemIndex }) => {
  const currentGroups = getGroupValues(slotValues);
  const currentValue = currentGroups[group.key] || { kind: group.kind };
  const currentItems = Array.isArray(currentValue.items)
    ? currentValue.items
    : [];
  return {
    ...slotValues,
    [SLOT_GROUP_VALUES_KEY]: {
      ...currentGroups,
      [group.key]: {
        ...currentValue,
        kind: group.kind,
        items: currentItems.filter((_, index) => index !== itemIndex),
      },
    },
  };
};

export const moveGroupAsset = ({
  slotValues = {},
  group,
  fromIndex,
  toIndex,
}) => {
  const currentGroups = getGroupValues(slotValues);
  const currentValue = currentGroups[group.key] || { kind: group.kind };
  const currentItems = Array.isArray(currentValue.items)
    ? [...currentValue.items]
    : [];
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= currentItems.length ||
    toIndex >= currentItems.length ||
    fromIndex === toIndex
  ) {
    return slotValues;
  }
  const [item] = currentItems.splice(fromIndex, 1);
  currentItems.splice(toIndex, 0, item);
  return {
    ...slotValues,
    [SLOT_GROUP_VALUES_KEY]: {
      ...currentGroups,
      [group.key]: {
        ...currentValue,
        kind: group.kind,
        items: currentItems,
      },
    },
  };
};

export const updateGroupTextValue = ({ slotValues = {}, group, text }) => {
  const currentGroups = getGroupValues(slotValues);
  return {
    ...slotValues,
    [SLOT_GROUP_VALUES_KEY]: {
      ...currentGroups,
      [group.key]: {
        ...(currentGroups[group.key] || {}),
        kind: group.kind,
        text,
      },
    },
  };
};

export const getSlotSelectedValue = (slot, slotValues = {}) => {
  const slotValue = slotValues?.[slot.key] || {};
  return (
    getSlotValueKeys(slot.kind)
      .map((key) => slotValue[key])
      .find((value) => value !== undefined && value !== null && value !== '') || ''
  );
};

export const toggleSingleSlotAsset = ({
  slotValues = {},
  slot,
  assetId,
  valueKey = getSlotValueKey(slot.kind),
}) => {
  const currentValue = getSlotSelectedValue(slot, slotValues);
  if (String(currentValue || '') === String(assetId || '')) {
    const nextValues = { ...slotValues };
    delete nextValues[slot.key];
    return nextValues;
  }

  const currentSlotValue = slotValues?.[slot.key] || {};
  return {
    ...slotValues,
    [slot.key]: buildSlotValue(
      slot,
      assetId,
      valueKey,
      currentSlotValue.config
    ),
  };
};

export const updateSlotValueConfig = ({
  slotValues = {},
  slot,
  configKey,
  value,
}) => {
  const currentSlotValue = slotValues?.[slot.key] || {};
  const nextConfig = {
    ...normalizeConfig(currentSlotValue.config),
  };
  const normalizedValue = normalizeRuntimeConfigValue(configKey, value);

  if (normalizedValue === undefined || normalizedValue === '') {
    delete nextConfig[configKey];
  } else {
    nextConfig[configKey] = normalizedValue;
  }

  const nextSlotValue = {
    ...currentSlotValue,
    kind: slot.kind,
  };

  if (Object.keys(nextConfig).length) {
    nextSlotValue.config = nextConfig;
  } else {
    delete nextSlotValue.config;
  }

  return {
    ...slotValues,
    [slot.key]: nextSlotValue,
  };
};

export const normalizeOrderImageAssets = (images = []) =>
  (Array.isArray(images) ? images : []).map((image, index) => {
    const url = getAssetUrl(image);
    const value = getAssetId(image);
    return {
      ...image,
      id: value || `order-image-${index}`,
      mediaType: VIDEO_PRODUCTION_SLOT_KIND.IMAGE,
      source: 'order',
      value,
      valueKey: 'image_id',
      url,
      coverUrl: url,
      label: getAssetLabel(image, `照片 ${index + 1}`),
      categoryId: ORDER_ASSET_CATEGORY_ID,
      categoryName: '订单素材',
    };
  });

export const normalizeOrderVideoAssets = (videos = []) =>
  (Array.isArray(videos) ? videos : []).map((video, index) => {
    const url = getAssetUrl(video);
    return {
      ...video,
      id: getAssetId(video) || url || `order-video-${index}`,
      mediaType: VIDEO_PRODUCTION_SLOT_KIND.VIDEO,
      source: 'order',
      value: url,
      valueKey: 'oss_url',
      url,
      coverUrl: getVideoCoverUrl(video),
      label: getAssetLabel(video, `视频 ${index + 1}`),
      categoryId: ORDER_ASSET_CATEGORY_ID,
      categoryName: '订单素材',
    };
  });

export const normalizeMaterialAssets = (materials = [], mediaType) =>
  (Array.isArray(materials) ? materials : [])
    .filter(
      (material) =>
        material?.media_type === mediaType &&
        material.is_active !== false &&
        getAssetUrl(material)
    )
    .map((material, index) => {
      const url = getAssetUrl(material);
      const isImage = mediaType === VIDEO_PRODUCTION_SLOT_KIND.IMAGE;
      return {
        ...material,
        id: material.id || url || `material-${mediaType}-${index}`,
        mediaType,
        source: 'material',
        value: url,
        valueKey: isImage ? 'media_url' : 'oss_url',
        url,
        coverUrl: isImage ? url : getVideoCoverUrl(material),
        label: getAssetLabel(material, `${isImage ? '图片' : '视频'} ${index + 1}`),
        categoryId: material.category_id || '',
        categoryName: material.category_name || '',
      };
    });

export const normalizeMaterialFontAssets = (materials = []) =>
  (Array.isArray(materials) ? materials : [])
    .filter(
      (material) =>
        material?.media_type === VIDEO_MATERIAL_MEDIA_TYPE.FONT &&
        material.is_active !== false &&
        getAssetUrl(material)
    )
    .map((material, index) => ({
      ...material,
      id: material.id || material.url || `material-font-${index}`,
      value: getAssetUrl(material),
      label: getAssetLabel(material, `字体 ${index + 1}`),
    }));

export const filterAssetCandidatesByCategory = (assets = [], categoryId = '') => {
  if (!categoryId) return assets;
  return (Array.isArray(assets) ? assets : []).filter(
    (asset) => String(asset.categoryId || '') === String(categoryId)
  );
};

export const buildAssetCategoryFilters = (assets = [], categories = []) => {
  const presentCategoryIds = new Set(
    (Array.isArray(assets) ? assets : [])
      .map((asset) => String(asset.categoryId || ''))
      .filter(Boolean)
  );
  const filters = [{ id: '', name: '全部' }];
  const seen = new Set(['']);

  if (presentCategoryIds.has(ORDER_ASSET_CATEGORY_ID)) {
    filters.push({ id: ORDER_ASSET_CATEGORY_ID, name: '订单素材' });
    seen.add(ORDER_ASSET_CATEGORY_ID);
  }

  (Array.isArray(categories) ? categories : []).forEach((category) => {
    const categoryId = String(category.id || '');
    if (
      categoryId &&
      presentCategoryIds.has(categoryId) &&
      !seen.has(categoryId)
    ) {
      filters.push({ id: category.id, name: category.name || categoryId });
      seen.add(categoryId);
    }
  });

  (Array.isArray(assets) ? assets : []).forEach((asset) => {
    const categoryId = String(asset.categoryId || '');
    if (categoryId && !seen.has(categoryId)) {
      filters.push({
        id: asset.categoryId,
        name: asset.categoryName || categoryId,
      });
      seen.add(categoryId);
    }
  });

  return filters;
};

export const validateSlotValues = ({
  slotConfig = [],
  slotGroupConfig = {},
  slotValues = {},
}) => {
  for (const slot of slotConfig) {
    const slotValue = slotValues?.[slot.key];
    if (slotValue?.kind && slotValue.kind !== slot.kind) {
      return `${slot.key} 类型不匹配`;
    }
    const maxLength = Number(slot.runtime?.max_length || 0);
    if (
      slot.kind === VIDEO_PRODUCTION_SLOT_KIND.TEXT &&
      maxLength > 0 &&
      getTextCharacterCount(slotValue?.text || getTextSlotDefaultContent(slot)) > maxLength
    ) {
      return `${slot.key} 不能超过 ${maxLength} 字`;
    }
  }
  for (const group of getSlotGroups(slotConfig, slotGroupConfig)) {
    const groupValue = getGroupValue(slotValues, group.key);
    if (groupValue?.kind && groupValue.kind !== group.kind) {
      return `${group.name || group.key} 类型不匹配`;
    }
    const items = getGroupSelectedItems(slotValues, group.key);
    if (items.length > group.slots.length) {
      return `${group.name || group.key} 选择数量不能超过 ${group.slots.length}`;
    }
    const maxLength = Number(group.runtime?.max_length || 0);
    const textParts =
      group.kind === VIDEO_PRODUCTION_SLOT_KIND.TEXT
        ? splitTextBySlotCount(groupValue?.text, group.slots.length)
        : [];
    const defaultTextParts =
      group.kind === VIDEO_PRODUCTION_SLOT_KIND.TEXT && textParts.length === 0
        ? group.slots.map(getTextSlotDefaultContent).filter(Boolean)
        : [];
    if (
      group.kind === VIDEO_PRODUCTION_SLOT_KIND.TEXT &&
      textParts.length > group.slots.length
    ) {
      return `${group.name || group.key} 分段数量不能超过 ${group.slots.length}`;
    }
    if (
      group.kind === VIDEO_PRODUCTION_SLOT_KIND.TEXT &&
      maxLength > 0 &&
      [...textParts, ...defaultTextParts].some(
        (part) => getTextCharacterCount(part) > maxLength
      )
    ) {
      return `${group.name || group.key} 每段不能超过 ${maxLength} 字`;
    }
  }
  return '';
};
